import { mapUserDisplayNameToTextValue } from '../../../utils/generalUtils';
import TRANSLATE_KEYS from '../../../public/locales/translateKeys';
import i18n from '../../../i18n';
import { FilterOptionValues } from '../../types';

export interface FilterQueryParams {
  field: string;
  value: string[];
  operation: string;
}

export enum FilterPanelTasksFilterNames {
  ASSIGNEE = 'assignee',
  LOCALE = 'locale',
  GENRES = 'genres',
  DUE_DATE = 'dueDate',
  STATUS_CHANGE_DATE = 'stateChangeDate',
  TASK_STATUS = 'status',
  WORKFLOW_TYPE = 'workflowType',
  READ_FLAG = 'read_flag',
  placementTypes_text_full_match = 'placementTypes_text_full_match',
  keyword_text_general = 'keyword_text_general',
  classifier_text_general = 'classifier_text_general',
  project_text_general = 'project_text_general',
  request_type_text_general = 'request_type_text_general',
  lob_text_full_match = 'lob_text_full_match',
  revision_round_integer = 'revision_round_integer',
  backstage_task_id_text_general = 'backstage_task_id_text_general'
}

export enum FilterPanelTasksSelectorKeys {
  LOCALE = 'locales',
  TASK_STATUS = 'taskStatuses',
  WORKFLOW_TYPE = 'workflowTypes',
  GENRES = 'genres'
}

export enum FILTER_TYPES {
  DROPDOWN_SEARCH = 'DROPDOWN_SEARCH',
  API_SEARCH = 'API_SEARCH',
  CALENDAR_SEARCH = 'CALENDAR_SEARCH'
}

interface CalendarFilterColumnData {
  type: FILTER_TYPES.CALENDAR_SEARCH;
  selectedValues: Date[][];
}

interface RegularFilterColumnData {
  type: FILTER_TYPES.API_SEARCH | FILTER_TYPES.DROPDOWN_SEARCH;
  selectedValues: string[];
}

interface ApiSearchFilterColumnData {
  type: FILTER_TYPES.API_SEARCH;
  selectedValues: string[];
}

interface FilterColumnData {
  name: FilterPanelTasksFilterNames;
  itemName: string;
  selectorKey?: FilterPanelTasksSelectorKeys;
  options: FilterOptionValues;
  selectedValues: string[];
  isSingleValueFilter?: boolean;
  nextCursor?: string;
  totalOptions?: number;
  prepareData?: (data?: any[]) => void;
  loading?: boolean;
  envEnabled?: string[];
}

type WorklistFilterOption = {
  value: string;
  text: string;
};
export interface WorklistFilterColumnData {
  field: string;
  displayName: string;
  filterable: boolean;
  options: WorklistFilterOption[];
  sortable: boolean;
  stype: string;
  visible: boolean;
}

export type RegularFilterColumn = RegularFilterColumnData & FilterColumnData;
export type ApiSearchFilterColumn = ApiSearchFilterColumnData & FilterColumnData;
export type CalendarFilterColumn = CalendarFilterColumnData & FilterColumnData;
export type FilterColumn = RegularFilterColumn | CalendarFilterColumn | ApiSearchFilterColumn;

const filtersParameters = {
  options: [] as FilterOptionValues,
  selectedValues: []
};

const filters: FilterColumn[] = [
  {
    name: FilterPanelTasksFilterNames.ASSIGNEE,
    itemName: i18n.t(TRANSLATE_KEYS.filters.assignee),
    type: FILTER_TYPES.API_SEARCH,
    prepareData(data: any) {
      return mapUserDisplayNameToTextValue(data);
    },
    ...filtersParameters
  },
  {
    name: FilterPanelTasksFilterNames.LOCALE,
    itemName: i18n.t(TRANSLATE_KEYS.filters.locale),
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.LOCALE,
    ...filtersParameters
  },
  {
    name: FilterPanelTasksFilterNames.GENRES,
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    itemName: i18n.t(TRANSLATE_KEYS.filters.genres),
    selectorKey: FilterPanelTasksSelectorKeys.GENRES,
    ...filtersParameters
  },
  {
    name: FilterPanelTasksFilterNames.DUE_DATE,
    type: FILTER_TYPES.CALENDAR_SEARCH,
    itemName: i18n.t(TRANSLATE_KEYS.filters.dueDate),
    ...filtersParameters
  },
  {
    name: FilterPanelTasksFilterNames.STATUS_CHANGE_DATE,
    itemName: i18n.t(TRANSLATE_KEYS.filters.statusChangeDate),
    type: FILTER_TYPES.CALENDAR_SEARCH,
    isSingleValueFilter: true,
    ...filtersParameters
  },
  {
    name: FilterPanelTasksFilterNames.TASK_STATUS,
    itemName: i18n.t(TRANSLATE_KEYS.filters.taskStatus),
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.TASK_STATUS,
    ...filtersParameters
  },
  {
    name: FilterPanelTasksFilterNames.READ_FLAG,
    itemName: 'Read Status',
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    isSingleValueFilter: true,
    options: [
      {
        value: true,
        text: 'Read'
      },
      {
        value: false,
        text: 'Unread'
      }
    ],
    selectedValues: [],
    envEnabled: ['localhost', 'itms7']
  },
  {
    name: FilterPanelTasksFilterNames.WORKFLOW_TYPE,
    itemName: i18n.t(TRANSLATE_KEYS.filters.workflowType),
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.WORKFLOW_TYPE,
    ...filtersParameters
  },
  {
    itemName: 'Placement Type',
    name: FilterPanelTasksFilterNames.placementTypes_text_full_match,
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.LOCALE,
    ...filtersParameters
  },
  {
    itemName: 'Keyword',
    name: FilterPanelTasksFilterNames.keyword_text_general,
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.LOCALE,
    ...filtersParameters
  },
  {
    itemName: 'Classifier',
    name: FilterPanelTasksFilterNames.classifier_text_general,
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.LOCALE,
    ...filtersParameters
  },
  {
    itemName: 'Project',
    name: FilterPanelTasksFilterNames.project_text_general,
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.LOCALE,
    ...filtersParameters
  },
  {
    itemName: 'Request Type',
    name: FilterPanelTasksFilterNames.request_type_text_general,
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.LOCALE,
    ...filtersParameters
  },
  {
    itemName: 'LOB',
    name: FilterPanelTasksFilterNames.lob_text_full_match,
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.LOCALE,
    ...filtersParameters
  },
  {
    itemName: 'Revision Round',
    name: FilterPanelTasksFilterNames.revision_round_integer,
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.LOCALE,
    ...filtersParameters
  },
  {
    itemName: 'ID',
    name: FilterPanelTasksFilterNames.backstage_task_id_text_general,
    type: FILTER_TYPES.DROPDOWN_SEARCH,
    selectorKey: FilterPanelTasksSelectorKeys.LOCALE,
    ...filtersParameters
  }
];

const filterPanelUtilsTasks = { filters };

export default filterPanelUtilsTasks;
